import i18n from '@/utils/i18n';
import { captureException } from '@/utils/sentry';
import {
  formatGrossProfitForSave,
  formatPriceForSave,
} from '@/utils/price-editing';
import { gql, useMutation } from '@apollo/client';
import {
  PermissionAction,
  PermissionSubject,
  SalesRepOverrideMode,
} from '@/enums';
import {
  PriceEditDisplayInfoDetail,
  SalesRep_UpdateGrossProfitPercentMutation,
  SalesRep_UpdateGrossProfitPercentMutationVariables,
  SalesRep_UpdatePriceMutation,
  SalesRep_UpdatePriceMutationVariables,
  VariantPackInfoContext_InfoFieldsFragment,
} from '@/types';
import { PriceEditInfo } from '@/adapters/offline-mode';
import {
  PriceEditor,
  PriceEditValidationError,
} from '@/domain/pricing/services/price-editor';
import {
  SalesRepGrossProfitValidationErrorCode,
  SalesRepPriceValidationErrorCode,
} from '@platform/errors';
import { transformToPriceEditorConfig } from '@/hooks/price-editing/use-price-editor-transformer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from './use-offline-mode-active';
import { useOfflineModeEnabled } from './use-offline-mode-enabled';
import { useOfflinePriceEdits } from './use-offline-price-edits';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { VARIANT_PACK_INFO_FRAGMENT } from '@/contexts/variant-pack-info-context-queries';

interface PriceEditingData {
  clearEdits: () => void;
  createConfirmHandler: (options?: {
    onError?: (error: unknown) => void;
    onSuccess?: () => void;
    showValidationErrors?: boolean;
  }) => () => Promise<boolean>;
  currentPriceInMicros: number | null;
  grossProfitDisplayValue: string;
  grossProfitInMicros: number | null;
  hasUnsavedChanges: boolean;
  isUpdatingGrossProfit: boolean;
  isUpdatingPrice: boolean;
  onHandQuantity: number | null | undefined;
  priceDisplayValue: string;
  priceEditor: PriceEditor;
  setSalesRepGrossProfitPercent: (value: string | null) => void;
  setSalesRepPriceInMicros: (value: string | null) => void;
  updatePriceEditCallback: () => Promise<
    VariantPackInfoContext_InfoFieldsFragment | null | undefined
  >;
  vpDisplayConfigs: PriceEditDisplayInfoDetail[] | undefined;
}

interface PriceEditingSettings {
  isPriceEditingEnabled: boolean;
  isSalesRepInfoDisabled: boolean;
  isSalesRepPeakPriceProtectionInEffect: boolean;
  salesRepGrossProfitEnabled: boolean;
  salesRepPricingEnabled: boolean;
  salesRepUpdatedByName: string | null;
}

export const UPDATE_SALES_REP_GROSS_PROFIT_PERCENT_MUTATION = gql`
  mutation SalesRep_UpdateGrossProfitPercent(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPacks: [SalesRepUpdateVariantPackGrossProfitInput!]!
    $deliveryDate: String
  ) {
    updateSalesRepGrossProfitPercentageOverrides(
      delivery_date: $deliveryDate
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      update_source: STOREFRONT
      variant_packs: $variantPacks
    ) {
      ...VariantPackInfoContext_InfoFields
    }
  }
  ${VARIANT_PACK_INFO_FRAGMENT}
`;

export const UPDATE_SALES_REP_PRICE_MUTATION = gql`
  mutation SalesRep_UpdatePrice(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPacks: [SalesRepUpdateVariantPackInput!]!
    $deliveryDate: String
  ) {
    updateSalesRepPrice(
      delivery_date: $deliveryDate
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      update_source: STOREFRONT
      variant_packs: $variantPacks
    ) {
      ...VariantPackInfoContext_InfoFields
    }
  }
  ${VARIANT_PACK_INFO_FRAGMENT}
`;

/**
 * Hook to support price editing.
 */
export const usePriceEditing = (
  variantPackUUID: string,
): PriceEditingData & PriceEditingSettings => {
  const {
    getVariantPackInfo,
    updateSalesRepVariantPackInfo,
    variantPackDetails,
    variantPackPriceEditSettings,
  } = useVariantPackInfoContext();
  const { selectedFulfillment } = useNewOrderActionsContext();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { selectedRestaurantRelationship, employeeCan } = useUserContext();
  const salesRepConfig = useDynamicConfigValue('sales_rep_config');
  const isOfflineModeActive = useOfflineModeActive();
  const isOfflineModeEnabled = useOfflineModeEnabled();
  const { setOfflinePriceEdits } = useOfflinePriceEdits();

  const variantPackInfo = getVariantPackInfo(variantPackUUID);
  const variantPackSettings = variantPackPriceEditSettings?.[variantPackUUID];
  const vpDisplayConfigs = variantPackDetails[variantPackUUID];

  const { showErrorToast } = useToasts();

  // Create Price Editor once and persist across re-renders
  const priceEditorRef = useRef<PriceEditor>(undefined);

  if (!priceEditorRef.current) {
    priceEditorRef.current = new PriceEditor(
      transformToPriceEditorConfig(
        variantPackInfo,
        selectedRestaurantRelationship,
        salesRepConfig,
        variantPackSettings,
      ),
    );
  }

  const priceEditor = priceEditorRef.current;

  // Update editor config when dependencies change
  useEffect(() => {
    priceEditor.updateConfig(
      transformToPriceEditorConfig(
        variantPackInfo,
        selectedRestaurantRelationship,
        salesRepConfig,
        variantPackSettings,
      ),
    );
  }, [
    priceEditor,
    variantPackInfo,
    selectedRestaurantRelationship,
    salesRepConfig,
    variantPackSettings,
  ]);

  const [editorState, setEditorState] = useState(() => priceEditor.getState());

  // Subscribe to price editor state changes
  useEffect(() => {
    return priceEditor.subscribe(setEditorState);
  }, [priceEditor]);

  // Reset on component unmount to avoid stale state
  useEffect(() => {
    return () => {
      setIsUpdatingGrossProfit(false);
      setIsUpdatingPrice(false);
      isSaving.current = false;
    };
  }, []);

  // Get data from price editor
  const isPriceEditingEnabled = priceEditor.isPriceEditingEnabled(
    employeeCan(PermissionAction.Write, PermissionSubject.SrtPriceEditing),
  );
  const isSalesRepInfoDisabled = priceEditor.isSalesRepInfoDisabled();
  const isSalesRepPeakPriceProtectionInEffect =
    priceEditor.isPeakPriceProtectionInEffect();
  const salesRepGrossProfitEnabled =
    selectedRestaurantRelationship.salesRepGrossProfitEnabled;
  const salesRepPricingEnabled =
    selectedRestaurantRelationship.salesRepPricingEnabled;
  const onHandQuantity = priceEditor.getOnHandQuantity();

  // Loading states
  const [isUpdatingGrossProfit, setIsUpdatingGrossProfit] = useState(false);
  const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
  const isSaving = useRef(false);

  const [updateSalesRepGrossProfitPercentage] = useMutation<
    SalesRep_UpdateGrossProfitPercentMutation,
    SalesRep_UpdateGrossProfitPercentMutationVariables
  >(UPDATE_SALES_REP_GROSS_PROFIT_PERCENT_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [
        SalesRepGrossProfitValidationErrorCode.SALES_REP_GROSS_PROFIT_TOO_HIGH,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_LOW,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_HIGH,
      ],
    },
  });

  const [updateSalesRepPrice] = useMutation<
    SalesRep_UpdatePriceMutation,
    SalesRep_UpdatePriceMutationVariables
  >(UPDATE_SALES_REP_PRICE_MUTATION, {
    context: {
      ignoreGraphQLErrorCodes: [
        SalesRepGrossProfitValidationErrorCode.SALES_REP_GROSS_PROFIT_TOO_HIGH,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_LOW,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_HIGH,
      ],
    },
  });

  // Commit functions
  const commitGrossProfitPriceEdit = useCallback(
    async (updatedGrossProfitInPercentage: number | null) => {
      const result = await updateSalesRepGrossProfitPercentage({
        variables: {
          deliveryDate: selectedFulfillment?.date,
          restaurantUUID: restaurantUUID!,
          supplierUUID: supplierUUID!,
          variantPacks: [
            {
              gross_profit_in_percentage:
                updatedGrossProfitInPercentage?.toString() ?? null,
              variant_pack_id: variantPackUUID,
            },
          ],
        },
      });
      return (
        result.data?.updateSalesRepGrossProfitPercentageOverrides[0] ?? null
      );
    },
    [
      restaurantUUID,
      selectedFulfillment?.date,
      supplierUUID,
      updateSalesRepGrossProfitPercentage,
      variantPackUUID,
    ],
  );

  const commitMicrosPriceEdit = useCallback(
    async (updatedPriceInMicros: number | null) => {
      const result = await updateSalesRepPrice({
        variables: {
          deliveryDate: selectedFulfillment?.date,
          restaurantUUID: restaurantUUID!,
          supplierUUID: supplierUUID!,
          variantPacks: [
            {
              sales_rep_price_in_micros: updatedPriceInMicros,
              variant_pack_id: variantPackUUID,
            },
          ],
        },
      });
      return result.data?.updateSalesRepPrice[0] ?? null;
    },
    [
      updateSalesRepPrice,
      selectedFulfillment?.date,
      restaurantUUID,
      supplierUUID,
      variantPackUUID,
    ],
  );

  const updatePriceEditCallback = useCallback(async () => {
    // Skip if already saving or there are no changes
    if (isSaving.current || !editorState.isDirty) {
      return null;
    }

    isSaving.current = true;
    setIsUpdatingGrossProfit(true);
    setIsUpdatingPrice(true);
    priceEditor.setSaveInProgress(true);

    try {
      const priceInMicros = formatPriceForSave(
        editorState.salesRepPriceInMicros,
      );
      const grossProfitPercent = formatGrossProfitForSave(
        editorState.salesRepGrossProfitPercent,
      );

      let updatedInfo: VariantPackInfoContext_InfoFieldsFragment | null = null;
      let offlinePriceEdit: PriceEditInfo | null = null;

      if (isOfflineModeActive && isOfflineModeEnabled) {
        offlinePriceEdit = {
          type: salesRepGrossProfitEnabled
            ? SalesRepOverrideMode.GROSS_PROFIT
            : SalesRepOverrideMode.PRICE,
          value: salesRepGrossProfitEnabled
            ? grossProfitPercent
            : priceInMicros,
        };
        await setOfflinePriceEdits(
          restaurantUUID!,
          supplierUUID!,
          variantPackUUID,
          offlinePriceEdit,
        );
      } else {
        updatedInfo = salesRepGrossProfitEnabled
          ? await commitGrossProfitPriceEdit(grossProfitPercent)
          : await commitMicrosPriceEdit(priceInMicros);
      }

      const result = updateSalesRepVariantPackInfo(
        variantPackUUID,
        updatedInfo,
        offlinePriceEdit,
      );

      if (result) {
        priceEditor.markAsSaved();
      }

      return result;
    } catch (error) {
      showErrorToast();
      captureException(error);
    } finally {
      isSaving.current = false;
      setIsUpdatingGrossProfit(false);
      setIsUpdatingPrice(false);
      priceEditor.setSaveInProgress(false);
    }
  }, [
    editorState.isDirty,
    editorState.salesRepPriceInMicros,
    editorState.salesRepGrossProfitPercent,
    priceEditor,
    isOfflineModeActive,
    isOfflineModeEnabled,
    updateSalesRepVariantPackInfo,
    variantPackUUID,
    salesRepGrossProfitEnabled,
    setOfflinePriceEdits,
    restaurantUUID,
    supplierUUID,
    commitGrossProfitPriceEdit,
    commitMicrosPriceEdit,
    showErrorToast,
  ]);

  const createConfirmHandler = useCallback(
    (
      options: {
        onError?: (error: unknown) => void;
        onSuccess?: () => void;
        showValidationErrors?: boolean;
      } = {},
    ) => {
      const { onSuccess, onError, showValidationErrors = true } = options;

      return async (): Promise<boolean> => {
        // No changes to save
        if (!editorState.isDirty) {
          return true;
        }

        // Validate current price edit
        const validation = priceEditor.validate(editorState);
        if (!validation.valid && showValidationErrors) {
          switch (validation.reason) {
            case PriceEditValidationError.EXCEEDS_FLOOR: {
              showErrorToast(
                i18n.t('toast.invalid_sales_rep_price_override', {
                  floorPrice: validation.bound,
                }),
              );
              break;
            }
            case PriceEditValidationError.EXCEEDS_CEILING: {
              showErrorToast(
                i18n.t('toast.invalid_sales_rep_price_override_too_high', {
                  ceilingPrice: validation.bound,
                }),
              );
              break;
            }
            case PriceEditValidationError.GROSS_PROFIT_TOO_HIGH: {
              showErrorToast(i18n.t('toast.gross_profit_percentage_too_high'));
              break;
            }
          }
          return false;
        }

        // Do the saving
        try {
          const result = await updatePriceEditCallback();
          if (result) {
            onSuccess?.();
            return true;
          }
          return false;
        } catch (error) {
          if (showValidationErrors) {
            showErrorToast();
            captureException(error);
          }
          onError?.(error);
          return false;
        }
      };
    },
    [editorState, priceEditor, showErrorToast, updatePriceEditCallback],
  );

  return {
    clearEdits: () => priceEditor.clearEdits(),
    createConfirmHandler,
    currentPriceInMicros: priceEditor.getCurrentPriceInMicros(),
    grossProfitDisplayValue: editorState.grossProfitDisplayText,
    grossProfitInMicros: priceEditor.getGrossProfitInMicros(),
    hasUnsavedChanges: editorState.isDirty,
    isPriceEditingEnabled,
    isSalesRepInfoDisabled,
    isSalesRepPeakPriceProtectionInEffect,
    isUpdatingGrossProfit,
    isUpdatingPrice,
    onHandQuantity,
    priceDisplayValue: editorState.priceDisplayText,
    priceEditor,
    salesRepGrossProfitEnabled,
    salesRepPricingEnabled,
    salesRepUpdatedByName:
      variantPackInfo?.salesRepInfo?.salesRepOverrideUpdatedByName ?? null,
    setSalesRepGrossProfitPercent: (value: string | null) =>
      priceEditor.updateGrossProfit(value),
    setSalesRepPriceInMicros: (value: string | null) =>
      priceEditor.updatePrice(value),
    updatePriceEditCallback,
    vpDisplayConfigs,
  };
};
